import React, { ReactElement, useCallback, useState } from 'react';
import { Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { useSearchParams, Navigate } from 'react-router-dom';
import { COGNITO_LOGIN } from 'graphql/auth/queries/authLogin';
import Loading from 'components/Messages/Loading/Loading';
import { useAuthContext } from 'context/auth.context';
import { GET_ONE_TENANT_FOR_EMAIL } from 'graphql/auth/queries/getOneTenantForEmail';
import { themeOptions } from 'theme';
import Form from 'components/Form/Form';
import { FormFieldInterface } from 'components/Form/Form.types';
import { LOGIN_LANDING_PAGE } from 'const/routes';
import { LoginChallengeResponse } from '__generated__/graphql';
import LoginChallenge from 'pages/Auth/Login/components/LoginChallenge/LoginChallenge';
import LoginContainer from './components/LoginContainer/LoginContainer';

const Login = (): ReactElement => {
  const [searchParams] = useSearchParams();
  const destination = searchParams.get('destination');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [challenge, setChallenge] = useState<LoginChallengeResponse | undefined>();
  const [tenantId, setTenantId] = useState<number | undefined>();
  const [noTenantFound, setNoTenantFound] = useState<boolean>(false);
  const [cognitoLogin, { loading, error }] = useLazyQuery(COGNITO_LOGIN);
  const [getOneTenantForEmail] = useLazyQuery(GET_ONE_TENANT_FOR_EMAIL);
  const {
    loading: authLoading,
    isSessionActive,
    login,
    lastTenantId,
    lastEmail,
  } = useAuthContext();

  const handleSubmit = useCallback(async () => {
    setNoTenantFound(false);
    setErrorMessage('');

    let tenantId = lastTenantId;
    //If the email address entered is different from the one we
    //stored when the tenant id was stored, then the tenant id is not valid
    if (!tenantId || lastEmail !== email) {
      const result = await getOneTenantForEmail({
        variables: { email },
      });
      tenantId = result.data?.getOneTenantForEmail.id;
    }

    if (tenantId) {
      cognitoLogin({
        variables: {
          input: {
            tenantId,
            userEmail: email,
            userPassword: password,
            destination,
          },
        },
      })
        .then((result) => {
          if (result?.data?.cognitoLogin) {
            if (result.data.cognitoLogin.data) {
              login(result.data.cognitoLogin.data);
            } else {
              setChallenge(result?.data?.cognitoLogin?.challenge);
              setTenantId(tenantId ?? undefined);
            }
          } else {
            setErrorMessage('The email or password is incorrect');
          }
        })
        .catch((error) => {
          setErrorMessage(error.message);
        });
    } else {
      setNoTenantFound(true);
      return;
    }
  }, [
    cognitoLogin,
    destination,
    email,
    getOneTenantForEmail,
    lastEmail,
    lastTenantId,
    login,
    password,
  ]);

  const handleUpdateEmail = useCallback((email: string) => {
    setEmail(email);
  }, []);

  const fields: FormFieldInterface[] = [
    {
      label: 'Email',
      onChange: (email) => handleUpdateEmail(email),
      type: 'email',
      value: email,
      containedLabel: true,
    },
    {
      label: 'Password',
      onChange: (pw) => setPassword(pw),
      type: 'password',
      value: password,
      containedLabel: true,
    },
  ];

  if (loading && authLoading) {
    return <Loading />;
  }

  if (isSessionActive()) {
    return <Navigate to={LOGIN_LANDING_PAGE} />;
  }

  return (
    <LoginContainer>
      <Typography component='h1' variant='h5'>
        {challenge && tenantId ? 'Set a New Password' : 'Log in'}
      </Typography>
      <div>
        {challenge && tenantId ? (
          <LoginChallenge
            tenantId={tenantId}
            email={email}
            challenge={challenge}
            onError={setErrorMessage}
            onNewChallenge={setChallenge}
          />
        ) : (
          <Form
            fields={fields}
            buttonText='Log in'
            onSubmit={handleSubmit}
            submitButtonProps={{
              fullWidth: true,
              color: 'primary',
              variant: 'contained',
            }}
            sx={{ p: 0 }}
          />
        )}
      </div>
      {noTenantFound && (
        <Typography color={themeOptions.colors.error900} mt={2}>
          * No Tenant found for Email
        </Typography>
      )}
      {error && (
        <Typography color={themeOptions.colors.error900} mt={2}>
          * {error?.message}
        </Typography>
      )}
      {!error && errorMessage && (
        <Typography color={themeOptions.colors.error900} mt={2}>
          * {errorMessage}
        </Typography>
      )}
      <Typography variant='body2' mt={4}>
        <a href='/forgotPassword'>Forgot your password?</a>{' '}
      </Typography>
      <Typography variant='body2' mt={4}>
        By clicking Log in, you agree to ReStore&rsquo;s{' '}
        <a href='/content/licence_agreement' target='_blank'>
          Licence Agreement
        </a>{' '}
        and{' '}
        <a href='/content/privacy' target='_blank'>
          Privacy Policy
        </a>
      </Typography>
    </LoginContainer>
  );
};

export default Login;
